import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Comments from "@publicPortal/components/comments/Comments";
import type { ICommentCard } from "@publicPortal/components/comments/commentCard/CommentCard";
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Tab, Tabs, TextField } from "@mui/material";
import { CommentsService } from "@publicPortal/services/CommentsService";
import "../PlanningApplication.css";
import CommentOpinionSelector, { OPINIONS, OpinionTypes } from "./components/CommentOpinionSelector";
import InputErrorMessage from "@mirage/components/inputErrorMessage/InputErrorMessage";
import NoCommentsMessage from "./components/NoCommentsMessage";
import IsAnonymousRadioButtons from "./components/IsAnonymousRadioButtons";
import { IPNotificationResponse } from "@shared/publicportal/entities/IPNotificationResponse";
import { IPComment } from "@shared/publicportal/entities/IPComment";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";
import NewCommentAttachments, { commentFiles } from "./components/NewCommentAttachments";
import { useRecoilValue } from "recoil";
import { GetPublicCommentsResponse, GetUserCommentsResponse } from "@shared/publicportal/contracts/Comments";
import useAlertService from "@mirage/hooks/useAlertService";
import { PublicPortalEntities } from "@shared/mirage/contracts/PublicPortalEntities";
import UserCustomProperty from "@shared/mirage/utils/getUserCustomProperty";
import NewComment from "@publicPortal/components/comments/newComment/NewComment";
import { IUser } from "@shared/mirage/core/contracts/IUser";
import { UploadedFile } from "@shared/mirage/core/contracts/UploadedFile";

interface CommentsTabProps {
    paId: number;
    newCommentsDisabled: boolean;
    user: Partial<IUser>;
}

const CommentsTabTypes = {
    USER_COMMENTS: "user-comments",
    PUBLIC_COMMENTS: "public-comments",
} as const;
export type CommentsTabTypes = typeof CommentsTabTypes[keyof typeof CommentsTabTypes];

const PACommentsTabContent: React.FC<CommentsTabProps> = ({ paId, newCommentsDisabled, user }) => {
    const commentsService = new CommentsService();
    const [commentOpinion, setCommentOpinion] = useState<OpinionTypes>(OPINIONS.regular.Neutral);
    const [publicComments, setPublicComments] = useState<GetPublicCommentsResponse>({ attachments: [[]], comments: [] } as GetPublicCommentsResponse);
    const [userComments, setUserComments] = useState<GetUserCommentsResponse>({ attachments: [[]], comments: [] } as GetUserCommentsResponse);
    const [isNeighbour, setIsNeighbour] = useState("no");
    const [remainAnonymous, setRemainAnonymous] = useState("no");
    const [newCommentContent, setNewCommentContent] = useState({ value: "", error: "" });
    const [currentTab, setCurrentTab] = useState<CommentsTabTypes>(CommentsTabTypes.PUBLIC_COMMENTS);
    const commentAttachments = useRecoilValue<UploadedFile[]>(commentFiles);
    const consulteeId = UserCustomProperty.getUserCustomPropertyValue(user, "consulteeId");
    const showAlert = useAlertService();

    useEffect(() => {
        getPublicComments();
        getUserComments();
    }, []);

    return (
        <Box className="comments__comments-container">
            <Tabs
                className="comments__comments-tabs"
                aria-label="Comments Navigation Tabs"
                variant="scrollable"
                scrollButtons="auto"
                value={currentTab}
                onChange={(event, newTab: CommentsTabTypes) => setCurrentTab(newTab)}
            >
                <Tab
                    disabled={!user?.id}
                    className="comments__comments-tabs_sub-tab"
                    key={"my-comments"}
                    disableRipple
                    label={"My Comments"}
                    value={CommentsTabTypes.USER_COMMENTS}
                />
                <Tab className="comments__comments-tabs_sub-tab" key={"public-comments"} disableRipple label={"Public Comments"} value={CommentsTabTypes.PUBLIC_COMMENTS} />
            </Tabs>
            {getTabContent()}
        </Box>
    );

    function setNewCommentDefaultValues() {
        setCommentOpinion("Neutral");
        setIsNeighbour("no");
        setRemainAnonymous("no");
        setNewCommentContent({ value: "", error: "" });
    }

    function getTabContent() {
        if (currentTab === "user-comments") {
            return user?.id ? renderMyComments() : null;
        }
        return renderPublicComments();
    }

    function renderMyComments() {
        if (user?.id && !consulteeId) {
            return (
                <>
                    {!newCommentsDisabled ? getNewCommentContent() : <NoCommentsMessage message="This application is closed for further comments." variant="disabled" />}
                    <Comments className="comments__my-comments" records={getUserCommentCards()} />
                </>
            );
        }
        return <NoCommentsMessage message="Only logged-in user that is not a consultee can comment the application." variant="disabled" />;
    }

    function renderPublicComments() {
        if (publicComments.comments?.length) {
            return <Comments records={getCommentCards()} />;
        }
        return <NoCommentsMessage message="There are no comments yet." variant="empty" />;
    }

    function getNewCommentContent() {
        return (
            <NewComment handleSave={async () => await handleNewCommentSave()}>
                <CommentOpinionSelector opinion={commentOpinion} changeOpinion={(newOpinion) => setCommentOpinion(newOpinion)} />
                {getNeighbourRadioButtons()}
                <IsAnonymousRadioButtons remainAnonymous={remainAnonymous} remainAnonymousOnChange={handleRemainAnonymousOnChange} />
                <TextField
                    error={!!newCommentContent.error}
                    helperText={<InputErrorMessage passed={!newCommentContent.error} message={newCommentContent.error} />}
                    multiline
                    minRows={4}
                    label="Your Comment"
                    value={newCommentContent.value}
                    onChange={(e) => handleContentChange(e.target.value)}
                />
                <NewCommentAttachments />
            </NewComment>
        );
    }

    function getNeighbourRadioButtons() {
        return (
            <FormControl className="comments__radio-buttons-container">
                <FormLabel id="neighbour-radio-buttons-label">Are you a neighbour?</FormLabel>
                <RadioGroup className="comments__radio-buttons-container" aria-labelledby="neighbour-radio-buttons-label" defaultValue="no" name="neighbour-radio-group">
                    <FormControlLabel value="no" control={<Radio checked={isNeighbour === "no"} onChange={handleIsNeighbourOnChange} />} label="No" />
                    <FormControlLabel value="yes" control={<Radio checked={isNeighbour === "yes"} onChange={handleIsNeighbourOnChange} />} label="Yes" />
                </RadioGroup>
            </FormControl>
        );
    }

    function handleContentChange(value: string) {
        setNewCommentContent(() => {
            return {
                error: "",
                value: value,
            };
        });
    }

    function handleIsNeighbourOnChange(event: React.ChangeEvent<HTMLInputElement>) {
        setIsNeighbour(event.target.value);
    }

    function handleRemainAnonymousOnChange(event: React.ChangeEvent<HTMLInputElement>) {
        setRemainAnonymous(event.target.value);
    }

    async function handleNewCommentSave() {
        if (!newCommentContent.value) {
            setNewCommentContent((prev) => {
                return { ...prev, error: "Enter your comment" };
            });
            return true;
        }
        const newComment = {
            content: newCommentContent.value,
            isUserANeighbour: isNeighbour === "no" ? 0 : 1,
            opinion: commentOpinion,
            remainAnonymous: remainAnonymous === "no" ? 0 : 1,
            paId: paId,
        } as IPComment;
        const createNewCommentResponse = await commentsService.create({ comment: newComment, attachments: commentAttachments });
        if (createNewCommentResponse.errorMsg) {
            showAlert(createNewCommentResponse.errorMsg, "error");
            return true;
        } else {
            await getUserComments();
            setNewCommentDefaultValues();
            return false;
        }
    }

    async function getPublicComments() {
        const response = await commentsService.getPublicComments(paId);
        setPublicComments(response);
    }

    async function getUserComments() {
        if (!user?.id) {
            return;
        }
        const response = await commentsService.getUserComments({ paId });
        setUserComments(response);
    }

    function getUserCommentCards(): ICommentCard[] {
        return userComments.comments?.map((comment, index) => {
            const commentCard: ICommentCard = {
                cardContent: comment.content,
                cardTitle: getUserCommentTitle(comment),
                cardTitleCaption: comment.address,
                commentKey: `${comment.id}-${comment.createdTime}`,
                subheader: `${DateFormatter.formatToDateTime(comment.createdTime)} ${comment.opinion || ""}`,
                attachments: {
                    attachmentsCounter: userComments.attachments[index]?.length,
                    commentId: comment.id,
                    entityName: PublicPortalEntities.P_COMMENT,
                },
            };
            return commentCard;
        });
    }
    function getCommentCards(): ICommentCard[] {
        return publicComments.comments?.map((comment, index) => {
            const commentCard: ICommentCard = {
                cardContent: comment.commentsToPublish,
                cardTitle: getPublicCommentTitle(comment),
                cardTitleCaption: comment.address,
                commentKey: `${comment.id}-${comment.createdTime}`,
                subheader: `${DateFormatter.formatToDateTime(comment.createdTime)} ${comment.opinion || ""}`,
                attachments: {
                    attachmentsCounter: publicComments.attachments[index]?.length,
                    commentId: comment.id,
                    entityName: PublicPortalEntities.P_NOTIFICATION_RESPONSE,
                },
            };
            return commentCard;
        });
    }

    function getUserCommentTitle(comment: IPComment) {
        if (comment.firstName && comment.surname && comment.userTitle) {
            return `${comment.userTitle} ${comment.firstName} ${comment.surname} `;
        }
        return "Anonym ";
    }

    function getPublicCommentTitle(comment: IPNotificationResponse) {
        if (comment.firstName && comment.surname && comment.title) {
            return `${comment.title} ${comment.firstName} ${comment.surname} `;
        }
        return "Anonym ";
    }
};

export default React.memo(PACommentsTabContent);
export type { CommentsTabProps };
